
import * as PR from "../../../prime-react/index";
import { useFormik } from "formik";
import * as Yup from "yup";
import { addCategoryAPI } from "../../../services/api";
import { useDispatch, useSelector } from "react-redux";
import { useMemo, useState } from "react";
import { useToast } from "../../../context/ToastContext";
import { processCategories } from "../../../utils/reuse";

const AddCategory = ({ addCategoryDisplay, setAddCategoryDisplay, setCategoryList, setTotalRecords, resetPageWithoutFetch }) => {
    const dispatch = useDispatch();
    const { showToast } = useToast();
    const sessionData = useSelector(state => state.auth.sessionData);
    const headers = useMemo(() => ({ sessionid: sessionData.sessionId }), [sessionData.sessionId]);
    const [iconPreview, setIconPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showError, setshowError] = useState('');

    const formik = useFormik({
        initialValues: {
            name: '',
            icon: null,
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .required('Name is required')
                .min(3, 'Name must be at least 3 characters')
                .max(100, 'Name cannot exceed 100 characters'),
            icon: Yup.mixed()
                .required('Icon is required')
                .test('fileType', 'Unsupported format', value =>
                    !value || ['image/jpeg', 'image/png', 'image/jpg'].includes(value.type)
                )
                .test('fileSize', 'Icon should be less than 2 MB', value => !value || value.size <= 2 * 1024 * 1024)
        }),
        onSubmit: values => {
            addCategoryHandler(values);
        },
    });

    const addCategoryHandler = (values) => {
        const payload = {
            name: values.name
        };
        const formData = new FormData();
        formData.append("data", JSON.stringify(payload));
        if (values.icon) {
            formData.append("attachments", values.icon);
        }
        setLoading(true);
        addCategoryAPI(formData, headers, dispatch, (response) => {
            if (response.result === "SUCCESS") {
                formik.resetForm();
                setIconPreview(null);
                showToast("success", "Category created successfully");
                setAddCategoryDisplay(false);
                const data = response.data?.categories || [];
                const total = response.data?.total || 0;
                setTotalRecords(total);
                const processedData = processCategories(data);
                // Find the record with the latest createdAt date
                const latestRecord = processedData.reduce((latest, current) => {
                    const latestDate = new Date(latest.category.createdAt);
                    const currentDate = new Date(current.category.createdAt);
                    return currentDate > latestDate ? current : latest;
                });
                const otherRecords = processedData.filter( // Filter the latest record from the list
                    item => item.category.id !== latestRecord.category.id
                );
                const finalData = [latestRecord, ...otherRecords]; //  latest record on top
                setCategoryList(finalData);
                resetPageWithoutFetch();
            } else {
                const error = response.error;
                setshowError(error?.errorMsg || error?.summary)
                setTimeout(() => {
                    setshowError('')
                }, 4000)
            }
            setLoading(false);
        });
    }

    const handleIconUpload = ({ files }) => {
        const file = files[0];
        formik.setFieldTouched("icon", true); // Mark field as touched
        if (file) {
            const isValidType = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
            const isValidSize = file.size <= 2 * 1024 * 1024; // 2MB
            formik.setFieldValue('icon', file);
            if (isValidType && isValidSize) {
                const reader = new FileReader(); // Set preview only when valid
                reader.onloadend = () => {
                    setIconPreview(reader.result);
                };
                reader.readAsDataURL(file);
            } else {
                setIconPreview(null); // If invalid, clear preview
            }
        }
    };

    return (
        <>
            <PR.Dialog header="Add Category" visible={addCategoryDisplay} modal={true}
                blockScroll={true} draggable={false}
                className="external-links-dialog"
                onHide={() => { setAddCategoryDisplay(false); formik.resetForm(); setIconPreview(null); }}>
                {/* Wrapper content */}
                <div className="content-wrapper">
                    <form autoComplete="off" onSubmit={formik.handleSubmit}>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="name">Name<span>*</span></label>
                            <PR.InputText
                                id="name"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur} aria-describedby="name"
                            />
                            {formik.touched.name && formik.errors.name && (
                                <small className="p-error">{formik.errors.name}</small>
                            )}
                        </div>
                        <div className="flex flex-column gap-2 mt-4">
                            <label htmlFor="upload">Upload an icon<span>*</span></label>
                            <div className="flex align-items-center gap-2">
                                <div className="icon-preview">
                                    {iconPreview ? (
                                        <img src={iconPreview} alt="Icon" className="category-icon" />
                                    ) : (
                                        <i className="pi pi-plus" />
                                    )}
                                </div>
                                <div className="choose-file-upload flex align-items-center">
                                    <PR.FileUpload
                                        mode="advanced"
                                        name="icon"
                                        customUpload
                                        uploadHandler={handleIconUpload}
                                        accept="image/png, image/jpg, image/jpeg"
                                        auto
                                        chooseLabel="Choose File"
                                        onSelect={handleIconUpload}
                                        onBlur={() => formik.setFieldTouched("icon", true)}
                                    />
                                    <span className="ml-2"> <span className="block">(PNG, JPG or JPEG)</span> File should be less than 2 MB.</span>
                                </div>
                            </div>
                            {formik.touched.icon && formik.errors.icon && (
                                <small className="p-error">{formik.errors.icon}</small>
                            )}
                        </div>
                        <div className="dialog-footer mt-4">
                            <div className="error-message">{showError}</div>
                            <div className="flex align-items-center">
                                <PR.Button label="Cancel" outlined className="logout-btns cancel" type="button" onClick={() => { setAddCategoryDisplay(false); formik.resetForm(); setIconPreview(null); }} />
                                <PR.Button loading={loading} label="Add" className="logout-btns click-btn" type="submit" disabled={!formik.isValid || !formik.dirty || loading || showError} />
                            </div>
                        </div>
                    </form>
                </div>
            </PR.Dialog>
        </>
    )
}

export default AddCategory;